import React from "react";
import {
  CardRarity,
  useGetMyCardsQuery,
  type OwnedCardTile,
} from "../api/respectCardsApi";
import CardTile from "./CardTile";
import { Button } from "../../../ui/components/Button";
import "../styles/cards.css";
import "../styles/cardsDeck.css";

/**
 * Phase 2 cards-launch — "Your collection" modal.
 *
 * Repurposed from the public catalog viewer (which exposed every card
 * including ones the user had never owned). Now strictly mirrors the
 * "Your cards" tile grid from CardsPage so callers (e.g. AOTD page
 * embed) can pop it open without revealing the locked silhouettes.
 *
 * Locked grid is intentionally NOT rendered here. Send users to
 * `/cards` for that.
 */

interface CardCatalogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** retained for API compat — modal always shows the user's cards. */
  showOwnership?: boolean;
}

type RarityFilter = "all" | CardRarity;

const RARITY_OPTIONS: { value: RarityFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "common", label: "Common" },
  { value: "rare", label: "Rare" },
  { value: "epic", label: "Epic" },
  { value: "legendary", label: "Legendary" },
];

export default function CardCatalogModal({ open, onOpenChange }: CardCatalogModalProps) {
  const { data, isLoading, isError } = useGetMyCardsQuery(undefined, { skip: !open });
  const [filter, setFilter] = React.useState<RarityFilter>("all");

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const tiles: OwnedCardTile[] = data?.tiles ?? [];

  const filtered = tiles.filter((t) => {
    if (filter === "all") return true;
    return (t.definition?.rarity || "").toLowerCase() === filter;
  });

  return (
    <div
      className="respect-modal"
      role="presentation"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="respect-modal__content respect-catalog-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Your collection"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="respect-catalog-modal__header">
          <h3 className="respect-modal__title">Your collection</h3>
          <button
            type="button"
            className="respect-catalog-modal__close"
            onClick={() => onOpenChange(false)}
            aria-label="Close collection"
          >
            ×
          </button>
        </div>

        <div className="respect-admin-gallery__chips" style={{ marginBottom: "0.75rem" }}>
          {RARITY_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt.value}
              className={`respect-admin-gallery__chip${filter === opt.value ? " is-active" : ""}`}
              onClick={() => setFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {isLoading ? <p className="respect-block__copy">Loading your collection…</p> : null}
        {isError ? (
          <p className="respect-block__copy">Could not load your collection.</p>
        ) : null}

        {!isLoading && !isError && filtered.length === 0 ? (
          <p className="respect-block__copy">
            You haven&apos;t collected any cards in this tier yet.
          </p>
        ) : null}

        <div className="cards-deck-grid">
          {filtered.map((tile) => (
            <CardTile key={tile.cardId} tile={tile} interactive={false} />
          ))}
        </div>

        <div className="respect-modal__actions" style={{ justifyContent: "flex-end" }}>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
